import { ReactNode, useMemo } from 'react';
import { Typography, List, ListItem, Grid } from '@mui/material';

const formatDate = (rawValue: string) => {
  const date = new Date(rawValue);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year}, ${hours}:${minutes}`;
};

// Helper function for formatting percentages and numbers
const formatNumber = (value: number, type: 'percent' | 'number') => {
  return type === 'percent' ? `${value.toFixed(2)}%` : value.toFixed(2);
};

// Helper function for applying color based on value
const applyAmountChangeFormatting = (
  value: number,
  formattedValue: ReactNode
) => {
  return (
    <span style={{ color: value >= 0 ? 'green' : 'red' }}>
      {formattedValue}
    </span>
  );
};

type StatField<T> = {
  label: string;
  type: 'number' | 'percent' | 'date' | 'custom' | 'quantity';
  formatting?: 'amountChange'; // Formatting for positive/negative values
  customValue?: (data: T) => string; // Custom formatting logic
  valueKey?: keyof T; // The key to use for extracting the raw value
};

export type StatsFieldMap<T> = {
  [K in keyof T | string /*TODO: remove the string*/]?: StatField<T>;
};

export type StatsProps<T extends Object = any> = {
  data: T;
  statsFieldMap: StatsFieldMap<T>;
  alignment?: 'horizontal' | 'vertical'; // Global value alignment
};

export const Stats = <T extends Object = any>({
  data,
  statsFieldMap,
  alignment = 'horizontal'
}: StatsProps<T>) => {
  const formattedStats = useMemo(() => {
    return Object.entries(statsFieldMap).map(([key, field]) => {
      if (!field) return null;

      // Use valueKey if provided, otherwise access rawValue directly
      const rawValue = field.valueKey
        ? data[field.valueKey]
        : data[key as keyof T];
      let formattedValue: ReactNode;

      // Apply custom formatting logic if provided
      if (field.customValue) {
        formattedValue = field.customValue(data);
      } else {
        // Apply formatting based on type
        if (field.type === 'percent' || field.type === 'number') {
          formattedValue = formatNumber(rawValue as number, field.type);
        } else if (field.type === 'quantity') {
          formattedValue = rawValue as number;
        } else if (field.type === 'date') {
          formattedValue = formatDate(rawValue as string);
        } else {
          formattedValue = rawValue as ReactNode;
        }
      }

      // Apply color formatting if 'amountChange' and rawValue is a number
      const valueElement =
        field.formatting === 'amountChange' && typeof rawValue === 'number'
          ? applyAmountChangeFormatting(rawValue as number, formattedValue)
          : formattedValue;

      return { key, field, valueElement };
    });
  }, [data, statsFieldMap]);

  return (
    <>
      <Typography
        id="statistics-modal-title"
        variant="h6"
        component="h2"
        sx={{ textAlign: 'center' }}
      >
        Statistics
      </Typography>
      <List>
        {formattedStats.map((item) =>
          item ? (
            <ListItem key={item.key} disableGutters>
              <Grid
                container
                spacing={1}
                direction={alignment === 'vertical' ? 'column' : 'row'}
                justifyContent="space-between"
                alignItems="center"
              >
                <Grid item>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {item.field.label}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {item.valueElement}
                  </Typography>
                </Grid>
              </Grid>
            </ListItem>
          ) : null
        )}
      </List>
    </>
  );
};
